// ---------------------------------------------------------------------------
// Interview timeline: per-program interview date -> countdown + a deterministic
// prep plan ("14 days out: 2 mocks left, drill your 3 weakest categories").
// ---------------------------------------------------------------------------

import type { DentalProgram } from '../types';

export interface InterviewCountdown {
  program: DentalProgram;
  /** YYYY-MM-DD */
  date: string;
  /** Whole days until the interview (0 = today). Negative dates are filtered out. */
  daysLeft: number;
  plan: string[];
}

function daysBetween(from: Date, toDateIso: string): number {
  const target = new Date(`${toDateIso}T00:00:00`);
  const start = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  return Math.round((target.getTime() - start.getTime()) / (24 * 60 * 60 * 1000));
}

export interface PrepContext {
  completedMocks: number;
  weakCategories: string[];
}

/** Deterministic prep advice for N days out. */
export function prepPlan(daysLeft: number, shortName: string, ctx: PrepContext): string[] {
  const weak = ctx.weakCategories.length
    ? ctx.weakCategories.slice(0, 3).join(', ')
    : 'your lowest-scoring categories';
  if (daysLeft <= 0) {
    return [
      `Interview day! Open Interview Day Mode for ${shortName} — best answers, talking points, and reminders.`,
      'No new practice today: review, breathe, arrive early.',
    ];
  }
  if (daysLeft <= 3) {
    return [
      `Final stretch: one short mock at "Realistic" difficulty, then stop drilling new questions.`,
      `Re-read your best answers and the ${shortName} talking points in Interview Day Mode.`,
      'Do one spoken run-through of "Tell me about yourself" and "Why this school?".',
    ];
  }
  if (daysLeft <= 7) {
    return [
      `Run a full-length mock for ${shortName} this week (target: at least ${Math.max(1, 2 - ctx.completedMocks)} more).`,
      `Drill your weak areas daily: ${weak}.`,
      'Practice due-for-review questions every day (Dashboard → Due today).',
    ];
  }
  if (daysLeft <= 14) {
    return [
      `Two weeks out: schedule 2 mocks for ${shortName} (${ctx.completedMocks} completed so far).`,
      `Alternate days: school-specific questions vs. ${weak}.`,
      'Generate model answers for any favorite questions still missing one.',
    ];
  }
  return [
    `${daysLeft} days out: build breadth — research more ${shortName} questions and practice a few per day.`,
    'Aim for one mock interview per week from now on.',
    `Track the rubric radar in Analytics and push up ${weak}.`,
  ];
}

/** Programs with an upcoming (today or future) interview date, soonest first. */
export function upcomingInterviews(
  programs: DentalProgram[],
  ctx: PrepContext,
  now: Date = new Date(),
): InterviewCountdown[] {
  return programs
    .filter((p): p is DentalProgram & { interviewDate: string } => !!p.interviewDate)
    .map((p) => ({
      program: p,
      date: p.interviewDate,
      daysLeft: daysBetween(now, p.interviewDate),
      plan: prepPlan(daysBetween(now, p.interviewDate), p.shortName, ctx),
    }))
    .filter((c) => c.daysLeft >= 0)
    .sort((a, b) => a.daysLeft - b.daysLeft);
}
